import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { Recall } from "@/Common/Api/Communications/Dto/Recall";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SingleSelectAutoComplete } from "@/Common/Components/Inputs/SingleSelectAutocomplete";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { EmployeeHelper } from "@/Common/Helpers/EmployeeHelper";
import { Person } from "@/Common/Models/Person/Person";
import { CommunicationStateType } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CommunicationsHelper } from "@/Communications/Helpers/CommunicationsHelper";
import { CommunicationsTab_OptimizedV2 } from "../CommunicationsTab_OptimizedV2";

import "./RecallEdit_V2.css";

interface IRecallEdit_V2Props {
    onResponseCallback: () => void;
    savedState: RecallEdit_V2State;
}

class RecallEdit_V2State {
    public id: string;
    public selectedPatients: Person[];
    public descriptionText: string;
    public selectedDescriptions: KeyValuePair<string, string>[];
    public selectedClinicians: Person[];
    public selectedSpecializationId: string;
    public specializations: FlatLookupView[];
    public dueDate: Date;
    public selectedPriorityId: string;
    public priorities: FlatLookupView[];
    public notes: string;
    public isClickableTextShown: boolean;

    public isCreateLocked: boolean;

    public static getDefault(): RecallEdit_V2State {
        const s = new RecallEdit_V2State();
        s.id = sessionStore.newId().toString();
        s.selectedPatients = [];
        s.descriptionText = "";
        s.selectedDescriptions = [];
        s.selectedClinicians = [];
        s.selectedSpecializationId = "";
        s.specializations = CommunicationsHelper.GetRecallSpecializationsFromCache();
        s.priorities = CommunicationsHelper.GetRecallPrioritiesLookupsFromCache();
        s.selectedPriorityId = isArrayNullOrUndefinedOrEmpty(s.priorities) ? "" : s.priorities[0].LookupID;
        s.notes = "";
        s.isClickableTextShown = false;
        s.isCreateLocked = false;
        return s;
    }
}

class RecallEdit_V2 extends BaseReactComponent<IRecallEdit_V2Props, RecallEdit_V2State> {
    constructor(props: IRecallEdit_V2Props) {
        super(props);

        this.state = this.props.savedState || RecallEdit_V2State.getDefault();
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        const hasDescription = this.state.descriptionText.trim().length > 0;
        const hasPatients = this.state.selectedPatients.length > 0;
        const hasClinician = this.state.selectedClinicians.length > 0;

        const isDisabled = !hasPatients || !hasClinician || !hasDescription || this.state.isCreateLocked;

        return (
            <div className="workspace" onClick={() => this.closeClickableText()}>
                <div className="myle-page-header bottom-border">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <p className="header-font-color">{cacheStore.GetString("CommunicationsLabel")}</p>
                            <h3 className="thin-font">{cacheStore.GetString("NewRecallLabel")}</h3>
                        </div>
                    </div>
                </div>
                <div id="posref" className="ws-scrollable">
                    <div className="myle-doc-tb main-content myle-form documents">
                        <div className="row qr">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("PatientsLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 ">
                                <PersonSearchAutocompleteCustom
                                    valid={this.state.selectedPatients.length > 0}
                                    useBlurEvent={true}
                                    componentId="recall_create_addpatient"
                                    selectedItemList={this.state.selectedPatients.toImmutable()}
                                    onSelectionCallback={personItem => this.selectPatient(personItem)}
                                    onClearCallback={personItem => this.removePatient(personItem)}
                                    focusOnLoad={false}
                                    closeDropDownAfterSelection={true}
                                    dataCy="recallEdit-patient"
                                />
                            </div>
                        </div>
                        <div className="row qr">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("DescriptionLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 ">
                                <SingleSelectAutoComplete
                                    id={this.state.id}
                                    valid={hasDescription}
                                    useblurevent={true}
                                    componentId="recall_create_description"
                                    selectedItems={this.state.selectedDescriptions.toImmutable()}
                                    displayedTextValue={this.state.descriptionText}
                                    onSelecting={kvp => this.mutateState(s => s.descriptionText = kvp.Value)}
                                    focusOnLoad={false}
                                    onChangeText={text => this.mutateState(s => s.descriptionText = text)}
                                    closeDropDownAfterSelection={true}
                                    dataCy="recallEdit-description"
                                />

                            </div>
                        </div>
                        <div className="row qr">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("ResponsibleProvidersLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 ">
                                <PersonSearchAutocompleteCustom
                                    valid={this.state.selectedClinicians.length > 0}
                                    componentId="recall_clinician"
                                    searchStrategy={new ClinicianSearchStrategy(cacheStore.CliniciansAndEmployeeGroups)}
                                    selectedItemList={this.state.selectedClinicians.toImmutable()}
                                    onSelectionCallback={personItem => this.selectClinician(personItem)}
                                    onClearCallback={personItem => this.removeClinician(personItem)}
                                    disabled={false}
                                    allowMultipleItemSelection={true}
                                    closeDropDownAfterSelection={true}
                                    dataCy="recallEdit-clinician"
                                />
                            </div>
                        </div>
                        <div className="row qr">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("SpecialtyLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 ">
                                <select data-cy="recallEdit-speciality" onChange={(e: any) => this.onSpecializationChange(e.target.value)} className="form-control myle-control" value={this.state.selectedSpecializationId}>
                                    {this.state.specializations.map(spec => (
                                        <option key={spec.LookupID} value={spec.LookupID}> {cacheStore.GetLocalizedValueByLookupId(spec.LookupID)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="row qr">
                            <div className="col-lg-2 col-md-3 col-sm-12 label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("DueDateLabel")}</label>
                            </div>

                            <div className="col-lg-10 col-md-9 col-sm-12">
                                <DateSelector
                                    id="task_start_datey"
                                    cssClass=""
                                    date={this.state.dueDate}
                                    onChangeCallback={date => this.mutateState(s => s.dueDate = date)}
                                    dateFormat="yyyy/MM/dd"
                                    disabled={false}
                                    toDayEnabled={true}
                                    dataCy="recallEdit-dueDate"
                                />
                            </div>
                        </div>
                        <div className="row qr">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("PriorityLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 ">
                                <select data-cy="recallEdit-priority" onChange={(e: any) => this.onPriorityChange(e.target.value)} className="form-control myle-control" value={this.state.selectedPriorityId}>
                                    {this.state.priorities.map(p => (
                                        <option key={p.LookupID} value={p.LookupID}> {cacheStore.GetLocalizedValueByLookupId(p.LookupID)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="row qr">
                            <div className="col-lg-2 col-md-3 col-sm-12 label-text-alignment">
                                <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12">
                                <TextAreaAutoSize
                                    id="recall_create_note"
                                    ref="recall_create_note"
                                    value={this.state.notes}
                                    onTextChangeCallback={text => this.mutateState(s => s.notes = text)}
                                    maxHeight="205px"
                                    dataCy="recallEdit-notes"
                                />

                                {this.state.isClickableTextShown && (
                                    <div className="clickable-text-container displace-correction">
                                        <ClickableTextPanel
                                            cssClass="text-helper-container"
                                            context="RNOTES"
                                            inputElementId="recall_create_note"
                                            onCloseEvent={() => this.closeClickableText()}
                                        />
                                    </div>
                                )}
                                <div className="text-right">
                                    <a onClick={e => this.toggleClickableTextVisibility(e)}> {cacheStore.GetString("SelectFromListLabel")} </a>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
                <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                    <div className="col-lg-12 col-md-12 col-sm-12 text-right">
                        <ButtonControl
                            className="btn btn-default push-right-5"
                            label={cacheStore.GetString("CancelLabel")}
                            onClick={() => this.props.onResponseCallback()}
                        />
                        <ButtonControl
                            className="btn btn-default push-right-5"
                            label={cacheStore.GetString("ClearLabel")}
                            onClick={() => this.setState(RecallEdit_V2State.getDefault())}
                        />
                        <ButtonControl
                            className="btn btn-primary"
                            label={cacheStore.GetString("CreateLabel")}
                            disabled={isDisabled}
                            onClick={() => this.onCreateClick()}
                            dataCy="recallEdit-create"
                        />
                    </div>
                </div>
            </div>
        );
    }

    public componentWillUnmount(): void {
        setTimeout(() => sessionStore.dispatch(
            LocalOperations.Communications.CommunicationStatesSave,
            {
                type: CommunicationStateType.RecallEdit,
                state: this.state
            }
        ), 0);
    }

    private onSpecializationChange(id: string): void {
        this.mutateState(s => s.selectedSpecializationId = id);
    }

    private onPriorityChange(id: string): void {
        this.mutateState(s => s.selectedPriorityId = id);
    }

    private toggleClickableTextVisibility(e: React.MouseEvent): void {
        e.stopPropagation();
        this.mutateState(s => s.isClickableTextShown = !s.isClickableTextShown);
    }

    private onCreateClick(): void {
        this.mutateState(s => s.isCreateLocked = true, () => {
            let recall = new Recall();
            recall.DueDate = (this.state.dueDate == null) ? null : moment(this.state.dueDate).format("YYYY-MM-DD");
            recall.Type = this.state.descriptionText;
            recall.IsCompleted = false;
            recall.SpecializationLookupId = this.state.selectedSpecializationId;
            recall.PriorityLookupId = this.state.selectedPriorityId;
            recall.Notes = this.state.notes;

            const clinicianGroups = this.state.selectedClinicians.filter(x => x.IsPersonGroup);
            const patientIds = this.state.selectedPatients.map(x => x.ContactId);

            let clinicianIds = this.state.selectedClinicians.map(x => x.ContactId);

            // get clinicians in group
            for (let x of clinicianGroups) {
                if (x.GroupEmployeeModels && x.GroupEmployeeModels.length > 0) {
                    for (let ge of x.GroupEmployeeModels) {
                        const person = cacheStore.GetCliniciansPersonsByContactFacetId(ge);
                        clinicianIds.push(person.ContactId);
                    }
                }

                let idx = clinicianIds.indexOf(x.ContactId);
                clinicianIds.splice(idx, 1);
            }

            // ensure clinicians in groups are not in list twice
            clinicianIds = clinicianIds.distinct();

            Client.Postv2(
                ServerOperations.Server.Myle_UpsertRecall,
                {
                    Recall: recall,
                    PatientIds: patientIds,
                    PracticiansIds: clinicianIds
                },
                response => {
                    if (response) {
                        this.mutateState(s => s.isCreateLocked = false, () => this.props.onResponseCallback());
                        if (cacheStore.ClinicSettings.EnableAutofillRecipientInCommunicationTab) {
                            sessionStore.dispatch(
                                LocalOperations.Communications.SavePrefilledSelectedClinicians,
                                {
                                    type: CommunicationStateType.RecallEdit,
                                    selectedClinicians: this.state.selectedClinicians
                                }
                            );
                        }
                    }
                }
            );
        });
    }

    private selectClinician(personItem: PersonDropdownItem): void {
        this.mutateState(s => s.selectedClinicians = EmployeeHelper.selectClinician(s.selectedClinicians, personItem));
    }

    private removeClinician(personItem: PersonDropdownItem): void {
        if (!personItem || !personItem.Item)
            return;

        this.mutateState(s =>
            s.selectedClinicians = s.selectedClinicians.filter(x => x.ContactId != personItem.Item.ContactId)
        );
    }

    private selectPatient(personItem: PersonDropdownItem): void {
        if (personItem == null || personItem.Item == null)
            return;

        this.mutateState(s => {
            if (s.selectedPatients.indexOf(personItem.Item) == -1)
                s.selectedPatients.push(personItem.Item);
        });
    }

    private removePatient(personItem: PersonDropdownItem): void {
        if (!personItem || !personItem.Item)
            return;

        this.mutateState(s =>
            s.selectedPatients = s.selectedPatients.filter(x => x.ContactId != personItem.Item.ContactId)
        );
    }

    private closeClickableText(): void {
        this.mutateState(s => s.isClickableTextShown = false);
    }
}

export { RecallEdit_V2, RecallEdit_V2State };
